import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ContactForm from '../components/ContactForm';
import {
  RiRobot2Line,
  RiBrainLine,
  RiLineChartLine,
  RiMagicLine,
  RiCameraLensLine,
  RiDatabase2Line,
  RiShieldKeyholeLine,
  RiCloudLine
} from 'react-icons/ri';
import bgImage from '../assets/ai1.png';

const AIPage = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const aiServices = [
    {
      icon: <RiRobot2Line />,
      title: 'Custom LLM Solutions',
      description: 'Deploy domain-specific large language models with fine-tuning, RAG architectures, and API integrations (OpenAI, Anthropic, Mistral)',
      color: 'linear-gradient(135deg, #8b5cf6, #d946ef)'
    },
    {
      icon: <RiBrainLine />,
      title: 'Natural Language Processing',
      description: 'Advanced NLP for chatbots, semantic search, sentiment analysis, and document intelligence with transformer models',
      color: 'linear-gradient(135deg, #3b82f6, #0ea5e9)'
    },
    {
      icon: <RiCameraLensLine />,
      title: 'Computer Vision',
      description: 'Object detection, facial recognition, OCR, and video analytics powered by convolutional neural networks',
      color: 'linear-gradient(135deg,rgb(4, 71, 255),rgb(16, 177, 188))'
    },
    {
      icon: <RiLineChartLine />,
      title: 'Predictive Analytics',
      description: 'Time series forecasting, customer behavior prediction, and anomaly detection with ML models',
      color: 'linear-gradient(135deg, #10b981, #14b8a6)'
    },
    {
      icon: <RiMagicLine />,
      title: 'AI Automation',
      description: 'Intelligent process automation combining RPA with AI decision engines and workflow orchestration',
      color: 'linear-gradient(135deg,rgb(116, 11, 245),rgb(0, 156, 246))'
    },
    {
      icon: <RiDatabase2Line />,
      title: 'AI Data Pipelines',
      description: 'End-to-end MLOps solutions for data ingestion, feature engineering, and model deployment',
      color: 'linear-gradient(135deg, #6366f1, #8b5cf6)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '80px 20px',
        color: '#fff',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        backgroundAttachment: 'fixed',
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.92), rgba(15, 23, 42, 0.95)), url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        textAlign: 'center'
      }}
      ref={ref}
    >
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={containerVariants}
        style={{ maxWidth: '1280px', margin: '0 auto' }}
      >
        <motion.h1
          style={{
            fontSize: 'clamp(2.25rem, 3vw + 1rem, 3.5rem)',
            fontWeight: '700',
            marginBottom: '24px',
            background: 'linear-gradient(to right, #8b5cf6, #0ea5e9)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
          variants={itemVariants}
        >
          AI & Machine Learning Solutions
        </motion.h1>

        <motion.p
          style={{
            fontSize: '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 60px',
            color: '#d1d5db',
            lineHeight: '1.7'
          }}
          variants={itemVariants}
        >
          Transform your business with our <span style={{ fontWeight: '600', color: '#fff' }}>cutting-edge AI technologies</span> — from custom LLMs to predictive analytics and intelligent automation systems.
        </motion.p>

        <motion.div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '30px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}
          variants={containerVariants}
        >
          {aiServices.map((service, index) => (
            <motion.div
              key={index}
              style={{
                background: service.color,
                borderRadius: '16px',
                padding: '30px',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                color: '#fff',
                textAlign: 'left',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
              variants={itemVariants}
              whileHover={{
                y: -8,
                scale: 1.03,
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                marginBottom: '20px',
                background: 'rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
              }}>
                {service.icon}
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '15px'
              }}>{service.title}</h3>
              <p style={{
                fontSize: '1rem',
                color: '#e5e7eb',
                lineHeight: '1.6'
              }}>{service.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Enhanced NovaCrest Section - Now Mobile Friendly */}
        <motion.div
          style={{
            marginTop: '80px',
            background: 'linear-gradient(to right, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))',
            borderRadius: '24px',
            padding: 'clamp(30px, 5vw, 50px)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
            maxWidth: 'min(1000px, 90vw)',
            margin: '80px auto 0',
            width: '100%',
            boxSizing: 'border-box'
          }}
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
        >
          <h2 style={{
            fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
            fontWeight: '700',
            marginBottom: '24px',
            background: 'linear-gradient(to right, #8b5cf6, #0ea5e9)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            NovaCrest AI Excellence
          </h2>

          <p style={{
            fontSize: 'clamp(1rem, 3vw, 1.2rem)',
            color: '#d1d5db',
            maxWidth: '800px',
            margin: '0 auto 40px',
            lineHeight: '1.7',
            textAlign: 'center',
            padding: '0 10px'
          }}>
            Pioneering intelligent systems that transform business operations through cutting-edge artificial intelligence and machine learning technologies.
          </p>

          <ul style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
            gap: '20px',
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0 20px',
            listStyle: 'none'
          }}>
            {[
              "Robust LLMs for natural language processing and conversation",
              "Full-stack AI integration from cloud to edge devices",
              "Strategic partnerships with OpenAI, AWS SageMaker, Hugging Face",
              "SOC2 Type II, HIPAA, and GDPR compliant AI solutions",
              "Rapid prototyping of custom AI agents and LLM applications",
              "End-to-end MLOps pipelines with continuous monitoring",
              "Multimodal AI systems combining vision, language, and analytics",
              "Ethical AI frameworks with explainability and bias mitigation",
              "Real-time AI inference at global scale with <50ms latency"
            ].map((item, index) => (
              <motion.li
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '16px',
                  background: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: '12px',
                  borderLeft: '4px solid #8b5cf6',
                  transition: 'all 0.3s ease'
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  transition: { delay: 0.5 + index * 0.1 }
                }}
                whileHover={{ x: 5 }}
              >
                <span style={{
                  display: 'inline-block',
                  marginRight: '12px',
                  color: '#8b5cf6',
                  fontSize: '1.5rem'
                }}>•</span>
                <span style={{
                  color: '#e5e7eb',
                  lineHeight: '1.6',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}>{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </motion.div>

    </div>
  );
};

export default AIPage;